import { useState } from 'react';
import { Transition, Disclosure } from '@headlessui/react';
import tw, { styled } from 'twin.macro';
import { ChevronDownIcon, IdentificationIcon } from '@heroicons/react/24/outline';
import { useAuth, AdminPortal } from '@frontegg/react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';

import ArrowRightOnTrayIcon from '~/shared/assets/icons/arrow-right-on-tray.svg';
import Modal from '~/shared/components/Modal';
import Button from '~/shared/components/Buttons';
import { useXFetch } from '~/shared/utils/useXFetch';
import runtimeEnv from '~/shared/utils/runtimeEnv';
import { Tenants } from '~/shared/utils/types';
import { logout } from '~/shared/utils/logout';

export default function ProfileDropdown() {
  const [showLogOutModal, setShowLogOutModal] = useState(false);
  const { user } = useAuth();
  const isAdmin = !!user?.roles?.find((r) => r.key === 'Admin');

  const api = useXFetch();

  const { data: tenants } = useQuery({
    queryKey: ['getTenants'],
    queryFn: async () =>
      await api<Tenants>(
        `${runtimeEnv.fronteggBaseUrl}/frontegg/identity/resources/users/v3/me/tenants`
      ),
  });

  return (
    <Disclosure as="div" className="relative z-[10]" defaultOpen={false}>
      {({ open }) => (
        <>
          <Disclosure.Button className="w-full group">
            <div className="flex gap-[8px] items-center">
              <UserAvatar username={user?.name || ''} />
              <div className="flex gap-[4px] items-center">
                <div className="leading-[20px] text-[18px] text-white">{user?.name}</div>
                <ChevronDownIcon
                  className={clsx('w-[16px] h-[16px] text-white', open ? 'rotate-180' : '')}
                />
              </div>
            </div>
          </Disclosure.Button>

          <Transition {...transitionProps}>
            {open && (
              <Disclosure.Panel>
                <div className="absolute top-0 right-0 flex flex-col p-[17px] mt-[4px] border border-gray-200 rounded-[12px] gap-[13px] bg-white">
                  {' '}
                  <div className="leading-[20px] text-[18px] text-[#949EAC]">
                    {tenants?.activeTenant?.name}
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => AdminPortal.show()}
                      className="flex gap-[8px] cursor-pointer items-center"
                    >
                      <IdentificationIcon className="w-[24px] h-[24px] stroke-primary-500" />
                      <div className="leading-[20px] text-[15px] text-primary-500 whitespace-nowrap">
                        Admin Portal
                      </div>
                    </button>
                  )}
                  <button
                    onClick={() => setShowLogOutModal(true)}
                    className="flex gap-[8px] cursor-pointer items-center"
                  >
                    <ArrowRightOnTrayIcon className="w-[24px] h-[24px] stroke-primary-500" />
                    <div className="leading-[20px] text-[15px] text-primary-500 whitespace-nowrap">
                      Log Out
                    </div>
                  </button>
                </div>
              </Disclosure.Panel>
            )}
          </Transition>
          <LogOutModal
            showModal={showLogOutModal}
            onClose={() => setShowLogOutModal(false)}
            username={user?.name || ''}
          />
        </>
      )}
    </Disclosure>
  );
}

function LogOutModal({
  showModal,
  onClose,
  username,
}: {
  showModal: boolean;
  onClose: VoidFunction;
  username: string;
}) {
  return (
    <Modal showModal={showModal} size="medium" onClose={onClose}>
      <div className="w-full flex flex-col items-center mt-[48px] mb-[36px]">
        <div className="flex gap-[8px] items-center mb-[12px]">
          <UserAvatar username={username || ''} />
          <div className="leading-[20px] text-[18px] text-[#949EAC]">{username}</div>
        </div>

        <div className="leading-[24px] text-[24px] font-medium text-[#1D1D1D] mb-[43px]">
          Are you sure you want to Log out?
        </div>

        <div className="flex gap-[4px]">
          <Button className="w-[133px]" onClick={onClose}>
            Cancel
          </Button>
          <Button className="w-[133px]" variant="secondary" onClick={logout}>
            Log out
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function UserAvatar({ username }: { username: string }) {
  const initials = (
    username.split(' ').length === 1 ? [username, username.substring(1, 2)] : username.split(' ')
  )
    .map((s) => s.substring(0, 1))
    .join('');

  return (
    <AvatarContainer>
      <div className="leading-[11px] text-[10px] text-white">{initials}</div>
    </AvatarContainer>
  );
}

const AvatarContainer = styled.div(() => [
  tw`w-[32px] h-[32px] flex justify-center items-center bg-green rounded-full`,
]);

const transitionProps = {
  enter: 'transition ease-out duration-100',
  enterFrom: 'transform opacity-0 scale-95',
  enterTo: 'transform opacity-100 scale-100',
  leave: 'transition ease-in duration-75',
  leaveFrom: 'transform opacity-100 scale-100',
  leaveTo: 'transform opacity-0 scale-95',
};
